import { useEffect, useMemo, useState } from "react"
import { getSessionWeeks } from "@/lib/programs"
import { getLastRmMeasurementDate } from "@/lib/supabase"
import { parseLocalDate } from "@/lib/utils"
import type { ProgramBlockState } from "@/hooks/useProgramBlock"

/**
 * Calendrier des tests du bloc — le SEUL rythme que suivent à la fois les mesures
 * corporelles et les tests de force.
 *
 * Les deux sections du Profil affichaient une valeur sans dire de quand elle datait ni
 * quand la suivante était attendue. Elles partagent désormais cette échéance, tirée de
 * `programs.testing_sessions` (typiquement les séances 4, 20 et 48 en Débutant) plutôt que
 * d'un intervalle en semaines recalculé de son côté par chaque section.
 *
 * ⚠️ AUCUNE DATE CALENDAIRE N'EST PROJETÉE ICI, ET IL NE FAUT PAS EN RÉINTRODUIRE.
 * Une première version déduisait la date d'une échéance de `block_start_date + semaine × 7`,
 * ce qui suppose un rythme parfait d'une semaine de programme par semaine de calendrier.
 * Le rythme réel décroche : un client à la séance 37/48 début septembre aurait dû, à ce
 * rythme théorique, être à la 48 mi-juillet. Le « conseil » affiché était alors une date
 * PASSÉE — antérieure au dernier relevé du client. Les échéances futures ne s'expriment
 * donc qu'en numéro de séance et en semaine de programme, jamais en date. Les seules dates
 * affichées sont des dates réellement enregistrées (dernier relevé, dernier test).
 */
export interface TestingSchedule {
  /** Séance du prochain test, null s'il n'en reste plus dans le bloc. */
  nextTestSession: number | null
  /** Semaine de PROGRAMME où tombe ce test (pas une semaine de calendrier). */
  nextTestWeek: number | null
  /** Séance du dernier test déjà franchi, null avant le premier. */
  lastTestSession: number | null
  lastTestWeek: number | null
  /** Date du dernier test de force réellement enregistré dans rm_history. */
  lastRmDate: string | null
  loading: boolean
}

/**
 * Prend l'état du bloc en argument plutôt que d'appeler `useProgramBlock` lui-même :
 * la page Profil l'a déjà chargé, et un second appel doublerait ses requêtes pour rien.
 */
export function useTestingSchedule(block: ProgramBlockState): TestingSchedule {
  const [weekBySession, setWeekBySession] = useState<Map<number, number>>(new Map())
  const [lastRmDate, setLastRmDate] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const { programmeId, testingSessions } = block
  // `testingSessions` est un tableau recréé à chaque rendu du hook parent : on dépend de
  // son contenu sérialisé, sinon l'effet se relance en boucle.
  const sessionsKey = testingSessions.join(",")

  useEffect(() => {
    let mounted = true
    if (!programmeId) {
      setLoading(false)
      return
    }
    setLoading(true)
    Promise.all([
      getSessionWeeks(programmeId, sessionsKey ? sessionsKey.split(",").map(Number) : []),
      getLastRmMeasurementDate(),
    ])
      .then(([weeks, date]) => {
        if (!mounted) return
        setWeekBySession(weeks)
        setLastRmDate(date)
      })
      .catch((err) => console.error("[useTestingSchedule] chargement échoué :", err))
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [programmeId, sessionsKey])

  return useMemo(() => ({
    nextTestSession: block.nextTestSession,
    nextTestWeek: block.nextTestSession != null ? weekBySession.get(block.nextTestSession) ?? null : null,
    lastTestSession: block.lastTestSession,
    lastTestWeek: block.lastTestSession != null ? weekBySession.get(block.lastTestSession) ?? null : null,
    lastRmDate,
    loading: loading || block.loading,
  }), [block.nextTestSession, block.lastTestSession, block.loading, weekBySession, lastRmDate, loading])
}

/** Tolérance autour d'un test : un relevé fait dans les jours qui le précèdent en fait partie. */
const SAME_WEEK_TOLERANCE_DAYS = 7

/**
 * Un relevé est à jour s'il a été fait dans la même semaine que le test de force le plus
 * récent — même calendrier pour les deux sections, pas un rythme propre aux mesures.
 *
 * La comparaison se fait sur la DATE RÉELLE du dernier test (`rm_history`), jamais sur une
 * semaine de programme projetée en date : c'est précisément cette projection qui produisait
 * des échéances dans le passé. Deux dates réellement enregistrées se comparent sans
 * hypothèse sur le rythme du client.
 *
 * La tolérance de sept jours couvre le relevé fait juste avant le test plutôt que le jour
 * même. Tant qu'aucun test n'est enregistré, rien n'est en retard : il n'y a pas d'échéance.
 */
export function isMeasurementUpToDate(
  lastMeasurementDate: string | null,
  lastRmDate: string | null
): boolean {
  if (!lastRmDate) return true
  if (!lastMeasurementDate) return false
  const measured = parseLocalDate(lastMeasurementDate.split("T")[0])
  const tested = parseLocalDate(lastRmDate.split("T")[0])
  if (isNaN(measured.getTime()) || isNaN(tested.getTime())) return true
  tested.setDate(tested.getDate() - SAME_WEEK_TOLERANCE_DAYS)
  return measured.getTime() >= tested.getTime()
}
